import { defaultUserSelection } from "@/config/userSelection";
import {
  AGE_END,
  AGE_START,
  BASE,
  BRANDS,
  BUTTERLFY,
  DIFFERENTIATION,
  FILTERS,
  PROFILE_GROUP,
  PROFILE_METRIC,
  GROUPED_BY,
  MARKETS,
  MODE,
  PERCEPTUAL,
  PERIOD,
  PERIOD_END_DATE,
  PERIOD_START_DATE,
  PERIOD_TYPE,
  POINT_IN_TIME,
  POST_END_DATE,
  POST_PERIOD_TYPE,
  POST_START_DATE,
  PROFILE,
  QUESTION_OPTIONS,
  PROFILE_RESPONSE,
  SECONDARY_BRANDS,
  SEGMENTS,
  STAT_TEST,
  VISUALIZATION_TYPE,
  TRENDS_METRIC,
  TRENDS_RESPONSE,
  SCORE_TYPE,
  DEFAULT,
  URL_BY_BRAND,
  URL_BY_SEGMENT,
  URL_BY_MARKET,
} from "@/constant/constantsThrougoutProject";
import { STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION } from "@/hooks/useURLSync/utils";
import { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";
import type { NavigateFunction } from "react-router-dom";

export const getUrlParamsWhileSwitchingPath = (
  pathName: string,
  activeGroupDataIds: any,
  userSelection: any,
  screenLevelSelection: any,
) => {
  let currentMode = userSelection?.mode || "POINT_IN_TIME";
  const currentPathDetail = ROUTES_CHILDREN_LOOKUP[pathName];
  const currentId =
    typeof currentPathDetail?.id === "string" ? currentPathDetail.id : "";
  const currentScreenType = currentId.includes(URL_BY_SEGMENT)
    ? URL_BY_SEGMENT
    : currentId.includes(URL_BY_MARKET)
      ? URL_BY_MARKET
      : URL_BY_BRAND;

  let screenLevelSelectionDetail = screenLevelSelection[currentScreenType];
  const vizType = userSelection?.visualizationType;

  if (
    pathName?.toLowerCase()?.includes("emotional") &&
    STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION?.includes(
      vizType,
    ) &&
    screenLevelSelection[`${currentScreenType}_${currentMode}_${vizType}`]
  ) {
    screenLevelSelectionDetail =
      screenLevelSelection[`${currentScreenType}_${currentMode}_${vizType}`];
  } else if (
    STORING_SCREEN_LEVEL_CONTEXT_FOR_MODE_AND_VISUALIZATION?.includes(
      currentMode,
    ) &&
    screenLevelSelection[`${currentScreenType}_${currentMode}`]
  ) {
    screenLevelSelectionDetail =
      screenLevelSelection[`${currentScreenType}_${currentMode}`];
  }

  if (
    currentMode === POINT_IN_TIME &&
    pathName === `/emotional/${URL_BY_BRAND}` &&
    [PERCEPTUAL, BUTTERLFY, DIFFERENTIATION, PROFILE].includes(vizType)
  ) {
    currentMode = vizType;
  }

  const finalMapping: any = {};
  const keysToProcess = currentPathDetail?.urlKeysMapping?.[currentMode] || [];

  const getFallbackList = (key: string) => {
    let result = screenLevelSelectionDetail?.[key];
    if (!result || result.length === 0) {
      for (const screen of [URL_BY_BRAND, URL_BY_SEGMENT, URL_BY_MARKET]) {
        if (screen !== currentScreenType && (!result || result.length === 0)) {
          result = screenLevelSelection?.[screen]?.[key];
        }
      }
    }
    if (!result || result?.length === 0) {
      result = userSelection?.[key];
    }
    if (!result || result.length === 0) {
      result = activeGroupDataIds?.[key];
      if (
        currentScreenType === URL_BY_SEGMENT ||
        currentScreenType === URL_BY_MARKET
      ) {
        result = result?.slice(0, 1);
      }
    }
    return result || [];
  };

  keysToProcess.forEach((key: string) => {
    if (key === MARKETS) {
      let result = screenLevelSelectionDetail?.[key];
      if (!result || result.length === 0) {
        if (currentScreenType === URL_BY_MARKET) {
          result = activeGroupDataIds?.[key] || [];
        } else {
          result = getFallbackList(key);
        }
      }
      finalMapping[key] = result;
    } else if (key === BRANDS) {
      const result = getFallbackList(key);
      const secBrands = screenLevelSelectionDetail?.secondaryBrands;
      if (currentMode === BUTTERLFY) {
        finalMapping["secondaryBrands"] =
          secBrands?.length > 0 ? secBrands : result?.slice(1, 2) || [];
        finalMapping[key] = result?.slice(0, 1);
      } else if (currentMode === DIFFERENTIATION) {
        finalMapping["secondaryBrands"] =
          secBrands?.length > 0 ? secBrands : result?.slice(1) || [];
        finalMapping[key] = result?.slice(0, 1);
      } else {
        finalMapping[key] = result;
      }
    } else if (key === QUESTION_OPTIONS) {
      finalMapping[key] = Object.entries(userSelection[key] || {}).flatMap(
        ([val, items]: [string, any]) =>
          items?.map((item: any) => `${val}-${item}`) || [],
      );
    } else if (
      [
        PERIOD_START_DATE,
        PERIOD_END_DATE,
        POST_START_DATE,
        POST_END_DATE,
      ].includes(key)
    ) {
      finalMapping[key] = userSelection?.[key];
    } else if (key === AGE_START && userSelection?.ageRange?.length > 1) {
      finalMapping[AGE_START] = userSelection.ageRange[0];
      finalMapping[AGE_END] = userSelection.ageRange[1];
    } else if (key === SEGMENTS) {
      const result = currentPathDetail?.useSegmentSelect
        ? activeGroupDataIds?.activeSegments
        : screenLevelSelectionDetail?.segments;
      if (result?.length > 0) finalMapping[key] = result;
    } else if (key === FILTERS) {
      const result = userSelection[key];
      if (result?.length > 0) finalMapping[key] = result;
    } else if (
      [
        STAT_TEST,
        BASE,
        MODE,
        PERIOD_TYPE,
        PERIOD,
        POST_PERIOD_TYPE,
        GROUPED_BY,
        SCORE_TYPE,
        TRENDS_METRIC,
        TRENDS_RESPONSE,
        PROFILE_GROUP,
        PROFILE_METRIC,
        PROFILE_RESPONSE,
      ].includes(key)
    ) {
      let result = userSelection[key];
      if (!result || result.length === 0) {
        result = defaultUserSelection[key];
      }
      finalMapping[key] = result;
    } else if (key === VISUALIZATION_TYPE) {
      const result = userSelection[key];
      if (pathName !== `/emotional/${URL_BY_BRAND}`) {
        if (![BUTTERLFY, PERCEPTUAL, DIFFERENTIATION].includes(result)) {
          finalMapping[key] = result;
        }
      } else if (result?.length > 0) {
        finalMapping[key] = result;
      }
    }
  });
  return finalMapping;
};

export const buildNewURLQueryString = (
  result: Record<string, any>,
  mode: any = "POINT_IN_TIME",
  pathName?: any,
): URLSearchParams => {
  const updatedURL = new URLSearchParams();
  const currentPathDetail =
    ROUTES_CHILDREN_LOOKUP[pathName ? pathName : location.pathname];

  if (
    location.pathname === `/emotional/${URL_BY_BRAND}` &&
    mode === POINT_IN_TIME &&
    (result?.visualizationType === PERCEPTUAL ||
      result?.visualizationType === BUTTERLFY ||
      result?.visualizationType === DIFFERENTIATION)
  ) {
    mode = result.visualizationType;
  } else if (
    (location.pathname?.startsWith("/emotional") ||
      location.pathname?.startsWith("/advertising") ||
      location.pathname?.startsWith("/equity") ||
      location.pathname?.startsWith("/conversion") ||
      location.pathname?.startsWith("/visibility") ||
      location.pathname?.startsWith("/reputation")) &&
    mode === POINT_IN_TIME &&
    result?.visualizationType === PROFILE
  ) {
    mode = result.visualizationType;
  }

  currentPathDetail?.urlKeysMapping?.[mode]?.forEach((key: string) => {
    if (currentPathDetail?.multipleOptionsForKeys[mode]?.includes(key)) {
      let value = result[key]?.length > 0 ? result[key] : [];
      value = Array.isArray(value) ? value : [value];
      if (value?.length > 0) {
        value && value?.forEach((val: string) => updatedURL.append(key, val));
      }
    } else {
      if (typeof result[key] !== "string" && result[key]?.length > 0) {
        updatedURL.set(key, result[key][0]);
      } else if (typeof result[key] === "number" && result[key] > 0) {
        updatedURL.set(key, result[String(key)]);
      } else if (result[key]?.length > 0) {
        updatedURL.set(key, result[key]);
      }
    }
  });
  return updatedURL;
};

export const buildNewURLQueryStringFromReduxState = (
  result: Record<string, any>,
  mode: any = "POINT_IN_TIME",
  pathName?: any,
): URLSearchParams => {
  const updatedURL = new URLSearchParams();
  const currentPathDetail =
    ROUTES_CHILDREN_LOOKUP[pathName ? pathName : location.pathname];

  if (
    location.pathname === `/emotional/${URL_BY_BRAND}` &&
    mode === POINT_IN_TIME &&
    (result?.visualizationType === PERCEPTUAL ||
      result?.visualizationType === BUTTERLFY ||
      result?.visualizationType === DIFFERENTIATION)
  ) {
    mode = result.visualizationType;
  } else if (
    (location.pathname?.startsWith("/emotional") ||
      location.pathname?.startsWith("/advertising") ||
      location.pathname?.startsWith("/equity") ||
      location.pathname?.startsWith("/conversion") ||
      location.pathname?.startsWith("/visibility") ||
      location.pathname?.startsWith("/reputation")) &&
    mode === POINT_IN_TIME &&
    result?.visualizationType === PROFILE
  ) {
    mode = result.visualizationType;
  }

  currentPathDetail?.urlKeysMapping?.[mode]?.forEach((key: string) => {
    if (key === "questionOptions") {
      let questions: string[] = [];
      Object.keys(result[key] || {})?.map((questionKey: string) => {
        const currentValue = result[key]?.[questionKey];
        currentValue?.forEach((val: number) => {
          questions?.push(`${questionKey}-${val}`);
        });
      });
      questions?.forEach((q: string) =>
        updatedURL?.append("questionOptions", q),
      );
    } else if (currentPathDetail?.multipleOptionsForKeys[mode]?.includes(key)) {
      const value = result[key]?.length > 0 ? result[key] : [];
      if (value?.length > 0) {
        value.forEach((val: string) => updatedURL.append(key, val));
      }
    } else {
      if (typeof result[key] !== "string" && result[key]?.length > 0) {
        updatedURL.append(key, result[key][0]);
      } else if (typeof result[key] === "number" && result[key] > 0) {
        updatedURL.append(key, result[String(key)]);
      } else if (result[key]?.length > 0) {
        updatedURL.append(key, result[key]);
      }
    }
  });
  return updatedURL;
};

/**
 * Constructs a URL from a base, a path, and optional query parameters.
 *
 * @param base The base URL or path segment.
 * @param path The path segment to append.
 * @param params Optional query parameters. `null` and `undefined` values are excluded.
 * @returns The fully constructed URL.
 */
export const buildUrl = (
  base: string,
  path: string,
  params?: Record<
    string,
    string | number | boolean | (string | number)[] | null | undefined
  >,
): string => {
  const combinedPath = `${base.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;

  if (!params || Object.keys(params).length === 0) {
    return combinedPath;
  }

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined) {
      searchParams.append(key, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `${combinedPath}?${queryString}` : combinedPath;
};

/**
 * Parse URL search params and convert them to the correct data types
 */
export const parseURLParams = (
  searchParams: URLSearchParams,
): Record<string, string | number | string[] | number[]> => {
  const result: Record<string, string | number | string[] | number[]> = {};
  for (const [key] of searchParams.entries()) {
    if (key === QUESTION_OPTIONS) {
      result[key] = searchParams.getAll(key);
    } else if (
      key === BRANDS ||
      key === FILTERS ||
      key === MARKETS ||
      key === SEGMENTS ||
      key === QUESTION_OPTIONS ||
      key === SECONDARY_BRANDS
    ) {
      result[key] = searchParams.getAll(key).map((val) => Number(val));
    } else {
      result[key] = searchParams.get(key) as string;
    }
  }
  return result;
};

/**
 * Update URL without triggering navigation
 */
export const updateUrlParams = (
  newParams: Record<string, string | number | string[] | number[] | any>,
  navigate: NavigateFunction,
): void => {
  const currentPathDetail = ROUTES_CHILDREN_LOOKUP[location.pathname];
  const currentParams = new URLSearchParams(location.search);
  const result: Record<string, any> = {};
  const currentViz =
    newParams?.visualizationType || currentParams?.get("visualizationType");
  let currentMode: string =
    (newParams["mode"] as any) || currentParams?.get("mode") || "POINT_IN_TIME";
  if (
    location.pathname === `/emotional/${URL_BY_BRAND}` &&
    currentMode === POINT_IN_TIME &&
    (currentViz === BUTTERLFY ||
      currentViz === PERCEPTUAL ||
      currentViz === DIFFERENTIATION)
  ) {
    currentMode = currentViz;
  } else if (
    (location.pathname?.startsWith("/emotional") ||
      location.pathname?.startsWith("/advertising") ||
      location.pathname?.startsWith("/equity") ||
      location.pathname?.startsWith("/conversion") ||
      location.pathname?.startsWith("/visibility") ||
      location.pathname?.startsWith("/reputation")) &&
    currentMode === POINT_IN_TIME &&
    currentViz === PROFILE
  ) {
    currentMode = currentViz;
  }
  for (const key of currentPathDetail?.urlKeysMapping[currentMode]) {
    if (currentPathDetail.multipleOptionsForKeys[currentMode].includes(key)) {
      if (newParams[key]) {
        result[key] = newParams[key];
      } else if (currentParams.getAll(key)) {
        result[key] = currentParams.getAll(key);
      }
    } else {
      if (newParams[key]) {
        result[key] = newParams[key];
      } else if (currentParams.get(key)) {
        result[key] = currentParams.get(key);
      }
    }
  }
  const updatedURL = buildNewURLQueryString(result, currentMode);
  // saveDataToStorageForUserSelection(newParams);
  // Update URL without navigation
  navigate(
    {
      pathname: window.location.pathname,
      search: updatedURL.toString(),
    },
    {
      replace: true,
    },
  );
};
